
import { VehicleCategories, VehicleTypes } from "@/api";
import { VehicleCategory } from "@/api/vehicleCategories";
import { VehicleType } from "@/api/vehicleTypes";
import PageTitle from "@/components/common/PageTitle.vue";
import { Component, Vue, Watch } from "vue-property-decorator";
import { namespace } from "vuex-class";

const Auth = namespace("auth");

const emptyCategory = (): VehicleCategory => ({
  name: "",
  vehicleTypeId: null
});

@Component({ components: { PageTitle } })
export default class VehicleCategoriesPage extends Vue {
  @Auth.Getter("getUser") getUser!: any;

  pageHeaders = [{ icon: "mdi-truck-outline", header: "Vehicle Categories" }];
  headers = [
    { text: "Category Name", value: "name", align: "start" },
    { text: "Type of Vehicle", value: "vehicleType", align: "start" },
    {
      text: "Actions",
      value: "actions",
      align: "start",
      sortable: false,
      width: "150px"
    }
  ];

  categories: VehicleCategory[] = [];
  vehicleTypes: VehicleType[] = [];
  loading = false;
  saving = false;
  showDialog = false;
  editingId: number | null = null;
  form: VehicleCategory = emptyCategory();
  errors: Record<string, string[]> = {};
  search: string | null = null;

  private searchTimer: number | null = null;

  get permissions(): string[] {
    return (this.getUser && this.getUser.permissions) || [];
  }
  get canView(): boolean {
    return this.permissions.includes("view_vehicle_categories");
  }
  get canCreate(): boolean {
    return this.permissions.includes("create_vehicle_categories");
  }
  get canEdit(): boolean {
    return this.permissions.includes("edit_vehicle_categories");
  }
  get canDelete(): boolean {
    return this.permissions.includes("delete_vehicle_categories");
  }

  async created(): Promise<void> {
    if (!this.canView) return;
    await Promise.all([this.load(), this.loadVehicleTypes()]);
  }

  async loadVehicleTypes(): Promise<void> {
    try {
      this.vehicleTypes = await VehicleTypes.list();
    } catch (e) {
      this.vehicleTypes = [];
    }
  }

  @Watch("search")
  onSearch(): void {
    if (this.searchTimer) window.clearTimeout(this.searchTimer);
    this.searchTimer = window.setTimeout(() => this.load(), 350);
  }

  async load(): Promise<void> {
    this.loading = true;
    try {
      this.categories = await VehicleCategories.list(this.search || null);
    } catch (e) {
      await this.$error(e);
    } finally {
      this.loading = false;
    }
  }

  openCreate(): void {
    this.editingId = null;
    this.form = emptyCategory();
    this.errors = {};
    this.showDialog = true;
  }

  openEdit(category: VehicleCategory): void {
    this.editingId = category.id || null;
    this.form = { ...category };
    this.errors = {};
    this.showDialog = true;
  }

  async save(): Promise<void> {
    this.saving = true;
    this.errors = {};
    try {
      const payload: VehicleCategory = {
        name: this.form.name,
        vehicleTypeId: this.form.vehicleTypeId || null
      };
      if (this.editingId) {
        await VehicleCategories.update(this.editingId, payload);
      } else {
        await VehicleCategories.create(payload);
      }
      this.showDialog = false;
      await this.load();
      await this.$success("<strong>Success!</strong> Category saved.");
    } catch (e) {
      this.errors = this.fieldErrors(e);
      if (!Object.keys(this.errors).length) await this.$error(e);
    } finally {
      this.saving = false;
    }
  }

  async confirmDelete(category: VehicleCategory): Promise<void> {
    const answer = await this.$dialog.error({
      text: `Delete "${category.name}"?`,
      title: "Warning",
      persistent: false,
      actions: { No: { text: "No" }, Yes: { color: "red", text: "Yes I do" } }
    });
    if (answer !== "Yes" || !category.id) return;
    try {
      await VehicleCategories.remove(category.id);
      await this.load();
      await this.$success("<strong>Success!</strong> Category deleted.");
    } catch (e) {
      await this.$error(e);
    }
  }

  fieldErrors(error: any): Record<string, string[]> {
    const bag = error?.response?.data?.errors || error?.errors;
    if (!bag) return {};
    const toCamelKey = (key: string): string =>
      key.replace(/_([a-z])/g, (_match, letter) => letter.toUpperCase());
    return Object.keys(bag).reduce((carry: Record<string, string[]>, key) => {
      carry[toCamelKey(key)] = bag[key];
      return carry;
    }, {});
  }
}
